import { Alert, Box, Button, Divider } from '@mui/material'
import i18next from 'i18next'
import { Navigate, Route, Routes } from 'react-router'
import { CommonSidebar, CommonSidebarLink } from 'components/common-sidebar'
import OCFFClientsComponent from 'components/cff/clients'
import OCFFFarmsComponent from 'components/cff/farms'
import OCFFieldsComponent from 'components/cff/fields'
import UploadBoundariesComponent from 'components/cff/upload-boundaries'
import { fetchFieldImport } from 'api/field'
import { closeModal, showModal } from 'actions/app'
import { getErrorMessageFromHttpError } from 'common/http-error'
import {
  FarmtrxIconUploadNew,
  FarmtrxIconClients,
  FarmtrxIconFarms,
  FarmtrxIconFields,
} from '@farmtrx/farmtrx-icons'
import { TEST_IDS } from 'test-ids'

function FieldImportAlert({ message }) {
  const dispatch = useDispatch()

  return (
    <Box>
      <Alert severity="error">{message}</Alert>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => dispatch(closeModal())}
          data-testid={TEST_IDS.cffAlertCloseButton}
        >
          {i18next.t('close')}
        </Button>
      </Box>
    </Box>
  )
}

export default function CffComponent() {
  const { t } = useTranslation()
  const [reader, setReader] = useState()
  const [isTransferringData, setTransferringData] = useState(false)
  const dispatch = useDispatch()
  const enabledCFF = useSelector(
    (state) => state.account.singleton.prefs?.cff?.enabled ?? false,
  )

  useEffect(() => {
    setReader(new FileReader())
  }, [])

  const onImportField = useCallback((fileList) => {
    let files = fileList
    if (files.length > 0) {
      let name = files[0].name
      if (name.indexOf('.zip') != -1) {
        convertFilePayload(files[0])
      } else {
        dispatch(
          showModal(
            <FieldImportAlert message={i18next.t('import_file_must_be_zip')} />,
            { title: i18next.t('field_boundary_import_error') },
          ),
        )
        setTransferringData(false)
      }
    }
  })

  const convertFilePayload = useCallback((files) => {
    reader.readAsDataURL(files)
    reader.onload = async function () {
      let fieldImportPayload = reader.result.split(',')[1]
      let fieldImportObj = {
        fileName: files.name,
        payload: fieldImportPayload,
      }
      await dispatch(fetchFieldImport(fieldImportObj)).catch((res) => {
        const errorMessage =
          getErrorMessageFromHttpError(res) || i18next.t('generic_error')
        dispatch(
          showModal(<FieldImportAlert message={errorMessage} />, {
            title: i18next.t('field_boundary_import_error'),
          }),
        )
        setTransferringData(false)
      })
    }
    reader.onerror = function (error) {
      console.log('Error: ', error)
    }
  })

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        overflowY: 'auto',
      }}
    >
      <CommonSidebar testId={TEST_IDS.cffSidebar}>
        {/* CFF tabs */}
        {enabledCFF ? (
          <>
            <CommonSidebarLink
              to="../clients"
              icon={<FarmtrxIconClients />}
              label={t('clients')}
              testId={TEST_IDS.cffClientsNav}
            />
            <Divider />
            <CommonSidebarLink
              to="../farms"
              icon={<FarmtrxIconFarms />}
              label={t('farms')}
              testId={TEST_IDS.cffFarmsNav}
            />
            <Divider />
          </>
        ) : (
          <></>
        )}
        <CommonSidebarLink
          to="../fields"
          icon={<FarmtrxIconFields />}
          label={t('fields')}
          testId={TEST_IDS.cffFieldsNav}
        />
        <Divider />
        <CommonSidebarLink
          to="../upload-boundaries"
          icon={<FarmtrxIconUploadNew />}
          label={t('upload_boundaries')}
          testId={TEST_IDS.cffUploadBoundariesNav}
        />
        <Divider />
      </CommonSidebar>
      {/* End */}
      <Routes>
        <Route
          exact
          path="/"
          element={
            enabledCFF ? (
              <Navigate to="/fields/clients" replace={true} />
            ) : (
              <Navigate to="/fields/fields" replace={true} />
            )
          }
        />

        {enabledCFF ? (
          <Route path="/clients" element={<OCFFClientsComponent />} />
        ) : (
          <Route
            path="/clients"
            element={
              <Box
                sx={{
                  m: 6,
                }}
              >
                <div>{t('no_permissions', { item: t('clients') })}</div>
              </Box>
            }
          />
        )}

        {enabledCFF ? (
          <Route path="/farms" element={<OCFFFarmsComponent />} />
        ) : (
          <Route
            path="/farms"
            element={
              <Box
                sx={{
                  m: 6,
                }}
              >
                <div>{t('no_permissions', { item: t('farms') })}</div>
              </Box>
            }
          />
        )}

        <Route path="/farms" element={<OCFFFarmsComponent />} />
        <Route path="/fields" element={<OCFFieldsComponent />} />
        <Route
          path="/upload-boundaries"
          element={
            <UploadBoundariesComponent
              onImportField={onImportField}
              isTransferringData={isTransferringData}
              setTransferringData={setTransferringData}
            />
          }
        />
      </Routes>
    </Box>
  )
}
